// Financial Freedom Calculator -- global currency support.
//
// The calculator serves a GLOBAL audience, so every monetary figure must be
// expressible in the visitor's own currency (not hard-coded to USD). The math
// itself is currency-agnostic (the engine only ever works with abstract
// amounts), so currency is a purely presentational concern handled entirely on
// the frontend: we pick a symbol for the slider prefixes and an ISO code for
// `Intl.NumberFormat` in the answer cards.

export interface CurrencyOption {
    code: string; // ISO 4217 code, used by Intl.NumberFormat.
    symbol: string; // Short glyph used as a slider prefix.
    label: string; // Human-readable option label.
}

export const CURRENCIES: CurrencyOption[] = [
    { code: 'USD', symbol: '$', label: 'USD — US Dollar' },
    { code: 'EUR', symbol: '€', label: 'EUR — Euro' },
    { code: 'GBP', symbol: '£', label: 'GBP — British Pound' },
    { code: 'CHF', symbol: 'CHF', label: 'CHF — Swiss Franc' },
    { code: 'JPY', symbol: '¥', label: 'JPY — Japanese Yen' },
    { code: 'AUD', symbol: 'A$', label: 'AUD — Australian Dollar' },
    { code: 'CAD', symbol: 'C$', label: 'CAD — Canadian Dollar' },
    { code: 'SGD', symbol: 'S$', label: 'SGD — Singapore Dollar' },
    { code: 'INR', symbol: '₹', label: 'INR — Indian Rupee' },
    { code: 'AED', symbol: 'د.إ', label: 'AED — UAE Dirham' },
];

export const DEFAULT_CURRENCY = 'USD';

/** The short symbol for a currency code (falls back to the code itself). */
export const currencySymbol = (code: string): string =>
    CURRENCIES.find((c) => c.code === code)?.symbol ?? code;

/** Locale-aware currency formatting; returns "N/A" for null/undefined. */
export const formatCurrency = (
    value: number | null | undefined,
    code: string,
): string =>
    value == null
        ? 'N/A'
        : new Intl.NumberFormat(undefined, {
              style: 'currency',
              currency: code,
              maximumFractionDigits: 0,
          }).format(value);
